import { Typography } from '@ag1/ui';
import { vars } from '@ag1/ui/theme';
import { PortableText } from '@portabletext/react';
import { StudioBaseImageV2 } from '../../StudioBaseImage/StudioBaseImage';
import { UnifiedButton, UnifiedButtonType } from '../../UnifiedButton';
import * as styles from './ComparisonCard.css';
import { StudioHotspotImageV2 } from '../../StudioHotspotImage';

const createBlockComponent = (isDarkTheme: boolean) => ({
  normal: ({ children }: { children?: React.ReactNode }) => (
    <Typography
      variant="subtitle"
      size="2"
      noPadding
      color={isDarkTheme ? 'brand-white' : 'black-new'}
      className={styles.paragraph}>
      {children}
    </Typography>
  )
});

interface ComparisonCardProps {
  headline: string;
  backgroundImage?: any;
  withMobileBackgroundImage?: boolean;
  mobileBackgroundImage?: any;
  tabletBackgroundImage?: any;
  bodyText: any[];
  icon?: any;
  ctaButtons?: Array<{
    _type: 'button';
    switchButton?: string;
    [key: string]: any;
  }>;
  ctaWidth?: 'full' | 'half';
  backgroundColor?:
    | 'agzPrimary'
    | 'blackSecondary'
    | 'greenPrimary'
    | 'greenSecondary'
    | 'stone'
    | 'stoneLight'
    | 'white';
  visibleOnPageLoad?: boolean;
}

export const ComparisonCard = ({
  headline,
  backgroundImage,
  withMobileBackgroundImage,
  mobileBackgroundImage,
  tabletBackgroundImage,
  bodyText,
  icon,
  ctaButtons,
  ctaWidth,
  backgroundColor: cardBackgroundColor,
  visibleOnPageLoad
}: ComparisonCardProps) => {
  const { colors } = vars;
  const colorMap: Record<string, string> = {
    agzPrimary: colors.background.agzPrimary,
    blackSecondary: colors.background.blackSecondary,
    greenPrimary: colors.background.greenPrimary,
    greenSecondary: colors.background.greenSecondary,
    stone: colors.background.stone,
    stoneLight: colors.background.stoneLight,
    white: colors.background.white
  };

  const backgroundColor = cardBackgroundColor ? colorMap[cardBackgroundColor] : undefined;

  // Determine if this is a dark theme for text color purposes
  const isDarkTheme =
    cardBackgroundColor === 'agzPrimary' ||
    cardBackgroundColor === 'blackSecondary' ||
    cardBackgroundColor === 'greenPrimary' ||
    cardBackgroundColor === 'greenSecondary';

  return (
    <div className={styles.card} style={backgroundColor ? { backgroundColor } : undefined}>
      {backgroundImage && (
        <div className={styles.backgroundImage}>
          <StudioBaseImageV2
            image={backgroundImage}
            mobileImage={withMobileBackgroundImage ? mobileBackgroundImage : undefined}
            tabletImage={withMobileBackgroundImage ? tabletBackgroundImage : undefined}
            className={styles.image}
            aspectRatio={100}
            eagerLoading={visibleOnPageLoad}
          />
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.textContent}>
          <div className={styles.headlineContainer}>
            <Typography
              variant="bodyMono"
              size="2"
              component="h3"
              color={isDarkTheme ? 'brand-white' : 'black-new'}
              noPadding
              className={styles.cardHeadline}>
              {headline}
            </Typography>
            {icon && (
              <StudioHotspotImageV2
                image={icon}
                className={styles.icon}
                eagerLoading={visibleOnPageLoad}
              />
            )}
          </div>
          <div>
            <PortableText
              value={bodyText}
              components={{
                block: createBlockComponent(isDarkTheme)
              }}
            />
          </div>
        </div>
      </div>
      {ctaButtons && ctaButtons.length > 0 && (
        <div className={styles.ctaContainer}>
          {ctaButtons.map((button) => (
            <div
              key={`cta-${button._type}-${button.switchButton || 'default'}-${
                button.openModalButton?.portableText || 'no-text'
              }`}
              className={
                ctaButtons.length === 1 && ctaWidth === 'full'
                  ? styles.fullWidthButton
                  : styles.buttonWrapper
              }>
              <UnifiedButton
                unifiedButton={button as unknown as UnifiedButtonType}
                sectionType="comparisonCards"
                className={styles.fullWidthButton}
                size="medium"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
